
import React from "react";
import orchestrateImg from "../../../assets/orchestration.png";

export function renderNetraaAPMReleaseNotes1() {
  return <RenderNetraaAPMReleaseNotes />;
}

function RenderNetraaAPMReleaseNotes() {
  const releaseMetrics = [
    { label: "Modules Updated", value: "15" },
    { label: "New Capabilities", value: "15" },
    { label: "Security & Governance", value: "1" },
    { label: "Impact Score", value: "9.1" },
  ];

  const releasePills = [
    "React Native application monitoring",
    "MySQL Orchestrator monitoring",
    "MongoDB observability",
    "Kubernetes visibility",
  ];

  const mongoMetricsTable = [
    { name: "meter_mongodb_cluster_uptime", desc: "Measures how long the MongoDB cluster has been running since its last restart." },
    { name: "meter_mongodb_cluster_connections", desc: "Total number of active client connections across the cluster." },
    { name: "meter_mongodb_cluster_document_total_qps", desc: "Number of document operations (reads, inserts, updates, deletes) processed per second across the cluster." },
    { name: "meter_mongodb_cluster_operation_total_qps", desc: "Total database operations processed per second, including queries, writes, commands, and administrative activities. Represents overall cluster workload." },
    { name: "meter_mongodb_cluster_cursor_total", desc: "Number of query result sets currently kept open by MongoDB for applications that are still reading data." },
    { name: "meter_mongodb_cluster_data_size", desc: "Total amount of business data stored in the cluster, excluding indexes." },
    { name: "meter_mongodb_cluster_index_size", desc: "Total storage consumed by indexes." },
    { name: "meter_mongodb_cluster_collection_count", desc: "Total number of collections (similar to tables in relational databases) stored across the cluster." },
    { name: "meter_mongodb_cluster_object_count", desc: "Total number of documents stored across all collections." },
    { name: "meter_mongodb_node_uptime", desc: "Measures how long an individual MongoDB node has been running since its last restart." },
    { name: "meter_mongodb_node_qps", desc: "Number of database requests processed per second by a node." },
    { name: "meter_mongodb_node_latency_rate", desc: "Average time taken by MongoDB to process requests." },
    { name: "meter_mongodb_node_op_rate", desc: "Rate at which database operations are executed on the node." },
    { name: "meter_mongodb_node_memory_usage", desc: "Percentage of system memory currently being used by MongoDB." },
    { name: "meter_mongodb_node_cpu_total_percentage", desc: "Percentage of CPU resources consumed by the MongoDB node." },
    { name: "meter_mongodb_node_network_bytes_in", desc: "Amount of incoming network traffic received by the node." },
    { name: "meter_mongodb_node_network_bytes_out", desc: "Amount of outgoing network traffic sent by the node." },
    { name: "meter_mongodb_node_memory_free_kb", desc: "Amount of physical memory still available on the server." },
    { name: "meter_mongodb_node_swap_memory_free_kb", desc: "Amount of unused swap space available on the server." },
    { name: "meter_mongodb_node_mem_virtual", desc: "Total virtual memory allocated by MongoDB, including reserved address space and memory-mapped files." },
    { name: "meter_mongodb_node_mem_resident", desc: "Amount of physical RAM currently occupied by MongoDB processes." },
    { name: "meter_mongodb_node_fs_used_size", desc: "Amount of disk space currently used by MongoDB data files, indexes, and logs." },
    { name: "meter_mongodb_node_fs_total_size", desc: "Total storage capacity available to MongoDB on the server." },
    { name: "meter_mongodb_node_connections", desc: "Number of active client connections currently established to the node." },
    { name: "meter_mongodb_node_active_total_num", desc: "Total number of database operations currently being executed." },
    { name: "meter_mongodb_node_active_reader_num", desc: "Number of read operations currently in progress. Represents the current read workload being handled by the node." },
    { name: "meter_mongodb_node_active_writer_num", desc: "Number of write operations currently in progress. Represents the current write workload being handled by the node." },
    { name: "meter_mongodb_node_transactions_active", desc: "Represents the current write workload being handled by the node." },
    { name: "meter_mongodb_node_transactions_inactive", desc: "Number of open transactions that are idle and not actively processing requests." },
    { name: "meter_mongodb_node_document_qps", desc: "Number of document-level operations processed per second by the node." },
    { name: "meter_mongodb_node_operation_qps", desc: "Total number of database operations processed per second by the node." },
    { name: "meter_mongodb_node_repl_operation_qps", desc: "Number of replication operations applied per second to keep replica set members synchronized. Indicates replication workload." },
    { name: "meter_mongodb_node_cursor", desc: "Number of query result sets currently kept open on the node." },
    { name: "meter_mongodb_node_asserts", desc: "Number of internal MongoDB assertion events detected. Frequent assertions may indicate software bugs, configuration problems, or operational issues requiring investigation." },
    { name: "meter_mongodb_node_repl_buffer_count", desc: "Number of replication operations currently waiting in the replication buffer before being applied." },
    { name: "meter_mongodb_node_repl_buffer_size", desc: "Amount of memory consumed by queued replication operations." },
    { name: "meter_mongodb_node_repl_buffer_size_max", desc: "Maximum capacity of the replication buffer. Used to evaluate whether replication buffering is approaching its configured limits." },
    { name: "meter_mongodb_node_queued_operation", desc: "Number of database requests waiting to be processed." },
    { name: "meter_mongodb_node_write_wait_num", desc: "Number of write requests currently waiting for locks or resources before execution." },
    { name: "meter_mongodb_node_write_wait_timeout_num", desc: "Number of write requests that exceeded their allowed wait time before execution." },
    { name: "meter_mongodb_node_write_wait_time", desc: "Average time write requests spend waiting before they can be processed." }
  ];

  const majorFeatureEnhancements = [
    {
      title: "React Native Monitoring Platform",
      items: [
        "Introduced React Native application monitoring and Real User Monitoring (RUM) capabilities.",
        "Added support for Node.js and NestJS application monitoring.",
      ],
    },
    {
      title: "License Management Enhancements",
      items: [
        "Enhanced license management capabilities with improved tracking of license consumption, allocation visibility, usage monitoring, and administrative controls.",
        "New dashboard capabilities provide better visibility into license utilization and simplify governance activities.",
      ],
    },
    {
      title: "Data Retention Management",
      items: [
        "Added configurable data retention capabilities that allow organizations to optimize storage utilization while maintaining historical monitoring data according to business requirements.",
        "Automated retention policies improve platform performance and simplify data lifecycle management.",
      ],
    },
    {
      title: "Threshold Management & Intelligent Visualization",
      items: [
        "Introduced application-specific threshold management with enhanced visual indicators and dynamic chart coloring.",
        "Users can quickly identify healthy, warning, and critical conditions, enabling faster issue detection and improved operational awareness.",
      ],
    },
    {
      title: "Application & Technology Management",
      items: [
        "Enhanced application organization through technology-based classification, categorization, and visual identification capabilities.",
        "These improvements simplify application discovery, administration, and monitoring activities.",
      ],
    },
    {
      title: "Problem Management & Service Monitoring",
      items: [
        "Introduced centralized problem management capabilities to streamline issue resolution.",
        "Improved alert configuration, notification readability, and operational visibility for better monitoring and faster response.",
        "Added log-based alerting and advanced threshold monitoring to strengthen proactive detection and system reliability.",
      ],
    },
    {
      title: "Root Cause Analysis & AI-Driven Analytics",
      items: [
        "Enhanced Root Cause Analysis capabilities through improved correlation between metrics, exceptions, and operational events.",
        "Expanded analytics capabilities help teams identify underlying causes of issues more effectively and accelerate troubleshooting activities.",
      ],
    },
    {
      title: "MongoDB Monitoring",
      items: [
        "Expanded monitoring support for MongoDB environments with enhanced visibility into cluster health, node performance, operational status, and telemetry data.",
        "These improvements provide database administrators with deeper operational insights.",
      ],
      hasMetricsTable: true
    },
    {
      title: "MySQL Orchestrator Monitoring & Management",
      items: [
        "Introduced centralized monitoring and management for MySQL database clusters, including topology visualization, replication monitoring, failover visibility, cluster health tracking, and maintenance management capabilities.",
      ],
      image: orchestrateImg
    },
    {
      title: "Netra AOP Enhancements",
      items: [
        "Enhanced application observability through endpoint grouping rules, searchable tags, and dynamic configuration management capabilities.",
        "These improvements improve trace analysis, dashboard clarity, troubleshooting efficiency, and operational flexibility.",
      ],
    },
    {
      title: "Agent Technology Innovations",
      items: [
        "Expanded monitoring coverage through support for Node.js and NestJS applications, enhanced .NET monitoring capabilities, improved log collection frameworks, Smart Agent automation, containerized application instrumentation, and automatic application server monitoring.",
      ],
    },
    {
      title: "Security & Platform Protection",
      items: [
        "Strengthened platform security through enhanced authentication, encryption, access control, vulnerability remediation, secure communication mechanisms, and improved protection against common web security threats.",
      ],
    },
    {
      title: "Kubernetes, ActiveMQ & RabbitMQ Monitoring",
      items: [
        "Expanded monitoring capabilities for Kubernetes clusters, ActiveMQ environments, and RabbitMQ messaging platforms, providing improved visibility into infrastructure health, resource utilization, and operational performance.",
      ],
    },
    {
      title: "Automation & Smart Onboarding",
      items: [
        "Introduced advanced onboarding automation, resource mapping, lifecycle management, and governance capabilities that reduce manual administration effort and improve operational efficiency.",
      ],
    },
  ];

  const notes = [
    "The release summary states that this release delivers major enhancements across mobile and application monitoring, agent technology and automatic instrumentation, database monitoring and cluster management, alerting and problem management, automation and platform administration, security and access management, infrastructure and middleware monitoring, data management and platform optimization for Orchestrator, and Netra AOP Enhancements.",
    "The introduction states that the release delivers significant enhancements across application performance monitoring, database observability, mobile monitoring, automation, security, analytics, and platform management.",
    "The release introduces several new monitoring capabilities, including React Native application monitoring, MySQL Orchestrator monitoring, MongoDB observability, Kubernetes visibility, and advanced agent automation.",
  ];

  return (
    <div>
      <main className="transition-opacity duration-300">
        <div className="mx-auto">
          <section className="relative scroll-mt-24" id="release-details">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                <span className="h-2 w-2 rounded-full bg-orange-500" />
                Release Notes 2026.05
              </div>

              <div className="flex items-center justify-between gap-4">
                <h1 className="text-3xl font-black tracking-tight sm:text-4xl">
                  <span className="bg-gradient-to-r from-orange-500 via-red-500 to-rose-600 bg-clip-text text-transparent">
                    Netraa APM Release Notes
                  </span>
                </h1>
              </div>

              <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg dark:text-slate-300">
                Netraa APM – Release Notes
              </p>
            </div>
          </section>

          <section className="scroll-mt-24 px-0 py-4" id="intro">
            <div className="rounded-3xl border border-orange-200/70 bg-gradient-to-br from-orange-50 via-white to-white p-6 shadow-sm dark:border-orange-500/15 dark:from-orange-500/10 dark:via-slate-950/30 dark:to-slate-950/30">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/25">
                  ✦
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                    Introduction
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    What this release brings to the platform.
                  </p>
                </div>
              </div>

              <p className="text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base">
                We are pleased to announce the April–May 2026 release of Netra APM,
                delivering significant enhancements across application performance
                monitoring, database observability, mobile monitoring, automation,
                security, analytics, and platform management. This release introduces
                several new monitoring capabilities, including React Native application
                monitoring, MySQL Orchestrator monitoring, MongoDB observability,
                Kubernetes visibility, and advanced agent automation. Major improvements
                have also been made to dashboards, alerting, root cause analysis,
                onboarding automation, license management, and security controls,
                helping organizations achieve greater operational visibility, faster
                troubleshooting, and improved platform governance.
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {releasePills.map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm dark:border-white/10 dark:bg-slate-900/50 dark:text-slate-200"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-10 w-full scroll-mt-24" id="analytics">
            <div className="mx-auto">
              <h4 className="mb-4 text-xl font-bold text-slate-900 dark:text-white">
                Release Analytics
              </h4>

              <div
                className="
      glass-card
      rounded-2xl
      border border-slate-200 dark:border-white/10
      bg-white/90 p-5 backdrop-blur-xl
      transition-all duration-500
      hover:-translate-y-1 hover:shadow-2xl
      dark:bg-white/5
      sm:p-6
    "
              >
                <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {releaseMetrics.map((item, i) => (
                    <div
                      key={i}
                      className="
              rounded-xl
              border border-slate-200 bg-white p-3
              shadow-sm
              transition-all duration-300
              hover:-translate-y-1 hover:scale-[1.02] hover:shadow-md
              dark:border-white/10 dark:bg-white/5
            "
                    >
                      <div className="mb-1 text-[11px] text-slate-500 dark:text-slate-400">
                        {item.label}
                      </div>

                      <div
                        className="
              text-lg font-bold text-orange-600
              dark:bg-gradient-to-r dark:from-orange-500 dark:to-rose-600
              dark:bg-clip-text dark:text-transparent
            "
                      >
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="mb-1 flex justify-between text-sm text-slate-700 dark:text-slate-300">
                      <span>Deployment Readiness</span>
                      <span className="font-semibold text-orange-500">98%</span>
                    </div>

                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                      <div
                        className="
              h-full w-[98%]
              bg-gradient-to-r from-orange-500 to-rose-600
              shadow-[0_0_8px_rgba(255,120,120,0.4)]
              transition-all duration-700
            "
                      />
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 flex justify-between text-sm text-slate-700 dark:text-slate-300">
                      <span>Health Meter</span>
                      <span className="font-semibold text-rose-500">Optimal</span>
                    </div>

                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                      <div
                        className="
              h-full w-full
              bg-gradient-to-r from-orange-500 to-rose-600
              shadow-[0_0_8px_rgba(255,120,120,0.4)]
              transition-all duration-700
          "z
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12 mt-12" id="overview">
            <h2 className="mb-4 text-xl font-bold text-slate-900 dark:text-white">
              Release Details
            </h2>
            <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-white/10">
              <table className="w-full text-left text-sm border-collapse">
                <thead className="bg-slate-50 dark:bg-white/5">
                  <tr>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                      Release Month
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                      Organization
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                      Prepared By
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                  <tr className="bg-white dark:bg-transparent">
                    <td className="px-5 py-4 font-medium text-orange-600 dark:text-orange-400 border border-slate-200 dark:border-white/10">
                      May 2026
                    </td>
                    <td className="px-5 py-4 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-white/10">
                      ApMoSys Products Pvt. Ltd.
                    </td>
                    <td className="px-5 py-4 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-white/10">
                      Netraa APM Product Team
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="scroll-mt-24" id="enhancements">
            <div className="mb-6 flex items-end justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Major Feature Enhancements
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Exact feature list from the release notes.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {majorFeatureEnhancements.map((item, index) => (
                <article
                  key={item.title}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/60"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-red-500 text-sm font-bold text-white shadow-md shadow-orange-500/20">
                      {index + 1}
                    </div>

                    <div className="min-w-0 w-full">
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">
                        {item.title}
                      </h3>

                      <ul className="mt-3 space-y-2">
                        {item.items.map((point) => (
                          <li
                            key={point}
                            className="flex gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300"
                          >
                            <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-orange-500" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>

                      {item.title === "MySQL Orchestrator Monitoring & Management" && (
                        <img
                          src={orchestrateImg}
                          alt="Orchestration"
                          className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md"
                        />
                      )}

                      {/* Integrated MongoDB Metrics Data Table with Inner/Outer Lines */}
                      {item.hasMetricsTable && (
                        <div className="mt-6 overflow-hidden rounded-xl border border-slate-300 shadow-sm dark:border-white/20">
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-left text-xs sm:text-sm">
                              <thead className="bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-slate-400">
                                <tr>
                                  <th className="border border-slate-300 px-4 py-3 font-semibold uppercase tracking-wider dark:border-white/20">
                                    Metric Name
                                  </th>
                                  <th className="border border-slate-300 px-4 py-3 font-semibold uppercase tracking-wider dark:border-white/20">
                                    Description
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-transparent">
                                {mongoMetricsTable.map((metric, mIdx) => (
                                  <tr
                                    key={mIdx}
                                    className="transition-colors hover:bg-slate-50/70 dark:hover:bg-white/5"
                                  >
                                    <td className="max-w-xs whitespace-pre-wrap break-all border border-slate-200 px-4 py-3 font-mono font-medium text-orange-600 dark:border-white/10 dark:text-orange-400">
                                      {metric.name}
                                    </td>
                                    <td className="border border-slate-200 px-4 py-3 text-slate-600 dark:border-white/10 dark:text-slate-300">
                                      {metric.desc}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="py-8" id="notes">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                Additional Notes
              </h2>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Extra content kept in the layout.
              </p>

              <ul className="mt-6 space-y-4">
                {notes.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-orange-500" />
                    <span className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

              <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 p-6 text-white shadow-lg">
                <h3 className="text-xl font-bold">Release Summary</h3>

                <p className="mt-3 text-sm leading-6 text-white/90">
                  We are pleased to announce the April–May 2026 release of Netra APM,
                  delivering significant enhancements across application performance
                  monitoring, database observability, mobile monitoring, automation,
                  security, analytics, and platform management.
                </p>

                <div className="mt-5 text-sm font-semibold">
                  Status: Major enhancements and platform improvements
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}